// ---------------------------------------------------------------------------
// MonthSelectorV2 — barra horizontal de seleção de meses (estilo V3).
// ---------------------------------------------------------------------------
// Porte visual do `MonthSelectorV3` (segmented control com cápsula branca
// no mês ativo, agrupamento por ano com cap-label, tracinho de status
// abaixo do label) para o vocabulário/cores de status da V2. NÃO depende
// de `StagePips` nem de `inputsV3StateRules.js` — usa o mesmo `STATUS_COLOR`
// historicamente adotado pela V2 (vide antigo `MonthGridSelector`).
//
// Características preservadas do V3:
// - Wrapper branco com borda discreta e scroll-x quando excede a largura;
// - Anos agrupados, cada um com seu trilho cinza e cap-label uppercase;
// - Cápsula branca + sombra leve no mês selecionado;
// - Tracinho colorido de ~14×3 abaixo do label (status agregado do mês);
// - Auto-scroll horizontal para manter o mês selecionado visível.
//
// Diferenças intencionais:
// - Sem `pendingDecisionsByMonth` / ponto laranja — V2 não possui essa
//   noção (intencional, definido na conversa de porte).
// - Sem `StagePips` — substituído por uma `<div>` simples com a cor do
//   status (paleta V2). Estado `running` ganha animação de listras leve
//   para sinalizar pipeline em execução.
// - Navegação por seta (esquerda/direita) preservada do `MonthGridSelector`
//   anterior para não regredir UX de teclado.
// ---------------------------------------------------------------------------

/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import cx from "classnames";

import {
  successColor,
  dangerColor,
  warningColor,
  grayColor,
  infoColor,
} from "../../../assets/jss/material-dashboard-pro-react";

// Mesma paleta de status historicamente usada pela V2 (chip do mês,
// MonthGridSelector etc.). Manter sincronizado.
const STATUS_COLOR = {
  complete: successColor[0],
  success: successColor[0],
  running: infoColor[0],
  waiting: grayColor[5],
  verifying: grayColor[5],
  error: dangerColor[0],
  system_error: dangerColor[0],
  unknown: warningColor[0],
};

// Abreviações de meses (mesmas chaves de `results.filter_bar.period.month_abbrevs`).
const MONTH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const useStyles = makeStyles(() => ({
  wrap: {
    padding: "2px 8px 6px",
    background: "#ffffff",
    border: `1px solid ${grayColor[5]}`,
    borderRadius: "6px",
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    overflowX: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": { scrollbarColor: `${grayColor[6]} transparent` },
    "&::-webkit-scrollbar": { height: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
      transition: "background 0.2s ease",
    },
    "&:hover::-webkit-scrollbar-thumb": { background: grayColor[6] },
  },
  row: {
    display: "inline-flex",
    alignItems: "flex-start",
    padding: "2px 2px 4px",
  },
  yearBlock: {
    display: "inline-flex",
    flexDirection: "column",
    "& + &": { marginLeft: "8px" },
  },
  yearCap: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: grayColor[0],
    fontWeight: 300,
    padding: "0 6px",
  },
  track: {
    display: "inline-flex",
    padding: "3px 0",
    background: grayColor[13],
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "7px",
  },
  yearTabs: { display: "inline-flex" },
  tab: {
    flexShrink: 0,
    padding: "4px 8px 10px",
    margin: "0 3px",
    fontSize: "12px",
    fontWeight: 400,
    color: grayColor[1],
    cursor: "pointer",
    position: "relative",
    whiteSpace: "nowrap",
    borderRadius: "5px",
    transition:
      "background 160ms ease, box-shadow 160ms ease, color 160ms ease",
    background: "transparent",
    border: "none",
    fontFamily: "inherit",
    outline: "none",
    "&:hover": { color: grayColor[7] },
    "&:focus-visible": {
      boxShadow: `0 0 0 2px ${grayColor[5]}`,
    },
    "&:disabled": {
      cursor: "default",
      "&:hover": { color: grayColor[1] },
    },
  },
  tabActive: {
    background: "#ffffff",
    color: grayColor[7],
    fontWeight: 500,
    boxShadow: "0 0 4px #00000033",
    "&:hover": { color: grayColor[7] },
  },
  // Wrapper que posiciona o tracinho de status centralizado abaixo do
  // nome do mês.
  pipWrap: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: "3px",
    display: "inline-flex",
    pointerEvents: "none",
  },
  pip: {
    width: "14px",
    height: "3px",
    borderRadius: "2px",
  },
  pipRunning: {
    backgroundImage: `linear-gradient(135deg, ${infoColor[0]} 0%, ${infoColor[0]} 40%, #ffffff80 50%, ${infoColor[0]} 60%, ${infoColor[0]} 100%)`,
    backgroundSize: "200% 100%",
    animation: "$monthPipRunning 1.2s linear infinite",
  },
  "@keyframes monthPipRunning": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
}));

function groupByYear(monthlyStatus) {
  const out = [];
  monthlyStatus.forEach((m, i) => {
    const last = out[out.length - 1];
    if (last && last.year === m.year) last.items.push({ month: m, idx: i });
    else out.push({ year: m.year, items: [{ month: m, idx: i }] });
  });
  return out;
}

export default function MonthSelectorV2({
  monthlyStatus,
  selectedMonth,
  onMonthSelect,
  disabled,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const selectedRef = useRef(null);

  const grouped = useMemo(() => groupByYear(monthlyStatus), [monthlyStatus]);

  const monthAbbrevs = useMemo(
    () =>
      Object.fromEntries(
        MONTH_KEYS.map((m) => [
          m,
          t(`results.filter_bar.period.month_abbrevs.${m}`),
        ]),
      ),
    [t],
  );

  // Mantém o tab selecionado visível horizontalmente (mesmo comportamento
  // do MonthSelectorV3).
  useEffect(() => {
    if (!selectedRef.current || !scrollRef.current) return;
    const wrap = scrollRef.current;
    const tab = selectedRef.current;
    const tabLeft = tab.offsetLeft;
    const tabRight = tabLeft + tab.offsetWidth;
    const viewLeft = wrap.scrollLeft;
    const viewRight = viewLeft + wrap.clientWidth;
    if (tabLeft < viewLeft || tabRight > viewRight) {
      wrap.scrollLeft = tabLeft - wrap.clientWidth / 2 + tab.offsetWidth / 2;
    }
  }, [selectedMonth, monthlyStatus.length]);

  // Navegação por teclado (preservada do antigo MonthGridSelector).
  const handleKeyNav = useCallback(
    (e) => {
      if (disabled || monthlyStatus.length === 0) return;
      if (e.key === "ArrowLeft") {
        const prev = selectedMonth - 1;
        if (prev >= 0) onMonthSelect(prev);
      } else if (e.key === "ArrowRight") {
        const next = selectedMonth + 1;
        if (next < monthlyStatus.length) onMonthSelect(next);
      }
    },
    [disabled, monthlyStatus, selectedMonth, onMonthSelect],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyNav);
    return () => window.removeEventListener("keydown", handleKeyNav);
  }, [handleKeyNav]);

  if (!monthlyStatus || monthlyStatus.length === 0) return null;

  return (
    <div className={classes.wrap} ref={scrollRef}>
      <div className={classes.row}>
        {grouped.map((y) => (
          <div key={y.year} className={classes.yearBlock}>
            <div className={classes.yearCap}>{y.year}</div>
            <div className={classes.track}>
              <div className={classes.yearTabs}>
                {y.items.map(({ month, idx }) => {
                  const isActive = idx === selectedMonth;
                  const statusKey = month.status;
                  const pipColor =
                    STATUS_COLOR[statusKey] || STATUS_COLOR.waiting;
                  const short = monthAbbrevs[month.month] || "";
                  const tooltip = (
                    <span>
                      <strong>{`${short} · ${month.year}`}</strong>
                      <br />
                      {t(`modeling_inputs_v2.month_status.${statusKey}`)}
                    </span>
                  );
                  return (
                    <Tooltip
                      key={idx}
                      title={tooltip}
                      placement="top"
                      enterDelay={300}
                      leaveDelay={0}
                    >
                      <button
                        type="button"
                        ref={isActive ? selectedRef : null}
                        disabled={disabled}
                        className={cx(classes.tab, {
                          [classes.tabActive]: isActive,
                        })}
                        onClick={() => {
                          if (!disabled) onMonthSelect(idx);
                        }}
                      >
                        {short}
                        <span className={classes.pipWrap}>
                          <span
                            className={cx(classes.pip, {
                              [classes.pipRunning]: statusKey === "running",
                            })}
                            style={
                              statusKey === "running"
                                ? undefined
                                : { background: pipColor }
                            }
                          />
                        </span>
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
